import React, { useState } from "react";
import { Copy, Check, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import { claimUpi } from "@/lib/pay";

// UPI has no callback, so this dialog is honest about what it can promise: it
// takes the payment claim and hands it to a human. Nothing unlocks here.
export default function UpiPay({ order, open, onOpenChange, onClaimed }) {
    const [ref, setRef] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    if (!order) return null;
    const rupees = (order.amount / 100).toFixed(2);

    const copyVpa = async () => {
        try {
            await navigator.clipboard.writeText(order.upi_vpa);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { toast.error("Could not copy — select the id and copy it by hand"); }
    };

    const claim = async () => {
        setBusy(true);
        try {
            await claimUpi(order.id, ref);
            onClaimed?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not record your payment");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="upi-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-plum">Pay ₹{rupees} by UPI</DialogTitle>
                </DialogHeader>

                <a href={order.upi_link} data-testid="upi-open-app"
                    className="btn-sticker w-full flex items-center justify-center gap-2 mt-2">
                    <Smartphone className="w-4 h-4" /> Open your UPI app
                </a>
                <p className="text-xs text-plum/50 text-center">
                    That button only works on a phone. On a computer, scan this with any UPI app.
                </p>

                {/* The deep link is inert on desktop, so the QR carries the same
                    payload — payee, amount and reference already filled in. */}
                <div className="flex justify-center py-2" data-testid="upi-qr">
                    <div className="bg-white p-3 rounded-2xl shadow-sm">
                        <QRCodeSVG value={order.upi_link} size={168} level="M" />
                    </div>
                </div>

                <div className="card-cloud p-4 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-wider text-plum/50">UPI id</div>
                        <div className="font-mono text-sm text-plum truncate" data-testid="upi-vpa">{order.upi_vpa}</div>
                    </div>
                    <button onClick={copyVpa} className="btn-sticker sky text-sm" data-testid="upi-copy-vpa">
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                </div>

                <div className="mt-2">
                    <label className="text-xs uppercase tracking-wider text-plum/50">
                        UPI reference number (optional)
                    </label>
                    <Input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={120} className="bg-white mt-1"
                        placeholder="e.g. 431299887766" data-testid="upi-ref-input" />
                    <p className="text-xs text-plum/50 mt-1">
                        Your UPI app shows this after paying. It helps us match your payment faster.
                    </p>
                </div>

                <Button onClick={claim} disabled={busy} data-testid="upi-claim-btn"
                    className="w-full rounded-full bg-bubblegum hover:bg-bubblegum/90 mt-2">
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    {busy ? "Saving…" : "I've paid"}
                </Button>
                <p className="text-xs text-plum/50 text-center">
                    A human checks the bank and confirms within 24 hours. You'll get an email
                    either way — and nothing starts until we've confirmed it.
                </p>
            </DialogContent>
        </Dialog>
    );
}
